import type { CSSProperties } from "react";
import {
  BANNER_DISPLAY_COUNT,
  bannerFileToSlug,
  bannerMaterialSrc,
} from "../../shared/bannerConstants";

/**
 * Product copy sourced from static HTML previews (dragonball / gundam / mtg /
 * pokemon / onepiece).
 *
 * Single source of truth for TCG campaigns:
 * - `slug` === banner filename stem (unique)
 * - Array order: newest first; first {@link BANNER_DISPLAY_COUNT} show on home banner
 * - Older entries stay for deep links / sitemap / prerender
 */

export type ProductAccent =
  | "red"
  | "orange"
  | "blue"
  | "indigo"
  | "emerald"
  | "green"
  | "purple"
  | "pink"
  | "amber";

export interface ProductSpecRow {
  label: string;
  value: string;
}

export interface ProductInformationEntry {
  id: string;
  /** Stable brand segment in /:brand/:slug (does not change across campaigns). */
  brand: string;
  /**
   * Unique URL slug — always the banner filename without extension.
   * New campaigns must use a new banner file → new slug.
   */
  slug: string;
  /** Banner image filename under {@link bannerMaterialSrc}. */
  bannerFile: string;
  /** ISO date `YYYY-MM-DD` used for News sorting (newest first). */
  releaseDate: string;
  accent: ProductAccent;
  heroImageUrl: string;
  badgeText: string;
  h1Line1: string;
  h1Accent: string;
  subtitleLine1: string;
  subtitleLine2: string;
  sectionTitle: string;
  sectionBody: string;
  specs: ProductSpecRow[];
  images: string[];
  asideTitle: string;
  asideBody: string;
  ctaText: string;
  filterHint: string;
}

const HERO_GRADIENT =
  "linear-gradient(to right, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.6) 50%, rgba(15, 23, 42, 0.1) 100%)";

export function heroBackgroundStyle(imageUrl: string): CSSProperties {
  return {
    background: `${HERO_GRADIENT}, url('${imageUrl}') 100% 100% / cover no-repeat fixed`,
  };
}

export const PRODUCT_ACCENT_CLASSES: Record<
  ProductAccent,
  {
    badge: string;
    accent: string;
    inputFocus: string;
    cta: string;
    /** Active thumbnail border on mobile gallery */
    thumbBorder: string;
  }
> = {
  red: {
    badge: "bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]",
    accent: "text-red-500",
    inputFocus: "focus:border-red-500 focus:ring-red-500",
    cta: "bg-red-600 hover:bg-red-500 shadow-[0_4px_15px_rgba(220,38,38,0.4)]",
    thumbBorder: "border-red-500",
  },
  orange: {
    badge: "bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]",
    accent: "text-orange-400",
    inputFocus: "focus:border-orange-500 focus:ring-orange-500",
    cta: "bg-orange-600 hover:bg-orange-500 shadow-[0_4px_15px_rgba(234,88,12,0.4)]",
    thumbBorder: "border-orange-400",
  },
  blue: {
    badge: "bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]",
    accent: "text-blue-500",
    inputFocus: "focus:border-blue-500 focus:ring-blue-500",
    cta: "bg-blue-600 hover:bg-blue-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-blue-500",
  },
  indigo: {
    badge: "bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]",
    accent: "text-indigo-500",
    inputFocus: "focus:border-indigo-500 focus:ring-indigo-500",
    cta: "bg-indigo-600 hover:bg-indigo-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-indigo-500",
  },
  emerald: {
    badge: "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]",
    accent: "text-emerald-500",
    inputFocus: "focus:border-emerald-500 focus:ring-emerald-500",
    cta: "bg-emerald-600 hover:bg-emerald-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-emerald-500",
  },
  green: {
    badge: "bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]",
    accent: "text-green-500",
    inputFocus: "focus:border-green-500 focus:ring-green-500",
    cta: "bg-green-600 hover:bg-green-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-green-500",
  },
  purple: {
    badge: "bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]",
    accent: "text-purple-500",
    inputFocus: "focus:border-purple-500 focus:ring-purple-500",
    cta: "bg-purple-600 hover:bg-purple-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-purple-500",
  },
  pink: {
    badge: "bg-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)]",
    accent: "text-pink-500",
    inputFocus: "focus:border-pink-500 focus:ring-pink-500",
    cta: "bg-pink-600 hover:bg-pink-500 shadow-[0_4px_15px_rgba(0,0,0,0.4)]",
    thumbBorder: "border-pink-500",
  },
  amber: {
    badge: "bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]",
    accent: "text-amber-400",
    inputFocus: "focus:border-amber-500 focus:ring-amber-500",
    cta: "bg-amber-600 hover:bg-amber-500 shadow-[0_4px_15px_rgba(217,119,6,0.4)]",
    thumbBorder: "border-amber-400",
  },
};

function campaign(
  partial: Omit<ProductInformationEntry, "slug" | "heroImageUrl"> & {
    bannerFile: string;
  },
): ProductInformationEntry {
  const slug = bannerFileToSlug(partial.bannerFile);
  return {
    ...partial,
    slug,
    heroImageUrl: `${bannerMaterialSrc}/${partial.bannerFile}`,
  };
}

/**
 * Newest campaigns first.
 * Home banner shows indices `[0, BANNER_DISPLAY_COUNT)`.
 * Remaining entries stay routable via `/:brand/:slug`.
 */
export const PRODUCT_INFORMATION_LIST: ProductInformationEntry[] = [
  // —— Currently featured on banner (5) ——
  campaign({
    id: "pokemon-30th-celebration",
    brand: "ptcg",
    bannerFile: "pokemon-30th-celebration.webp",
    releaseDate: "2026-09-16",
    accent: "amber",
    badgeText: "⏳ Releases 2026/09/16",
    h1Line1: "Mega Celebration",
    h1Accent: "「30th CELEBRATION」",
    subtitleLine1: "With Pokémon cards — then, now, and always.",
    subtitleLine2:
      "The Pokémon TCG 30th Anniversary Celebration — party with Pikachu!",
    sectionTitle: "Thirty Years of Wonder — The Celebration Begins",
    sectionBody:
      "The Pokémon Card Game 30th Anniversary Celebration arrives! Classic favorites like Pikachu, Mew, and Mewtwo gather once more. Rekindle the bonds of friendship and enjoy a celebration unlike any before!",
    specs: [
      { label: "Release date", value: "September 16, 2026 (Wed)" },
      { label: "Game series", value: "Pokémon Card Game MEGA" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Featuring 30th anniversary commemorative cards, Pikachu, and mythical Pokémon adventure cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/pokemon-30th-celebration-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/pokemon-30th-celebration-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/pokemon-30th-celebration-3.webp",
    ],
    asideTitle: "📍 Ready to join the celebration?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Pokémon products",
  }),
  campaign({
    id: "onepiece-eb05-heroines-vol2",
    brand: "onepiececardgame",
    bannerFile: "eb05-heroines-edition-vol2.webp",
    releaseDate: "2026-10-31",
    accent: "pink",
    badgeText: "⏳ Releases 2026/10/31",
    h1Line1: "[EB-05] Extra Booster",
    h1Accent: "「ONE PIECE Heroines Edition Vol.2」",
    subtitleLine1: "The second Extra Booster starring the heroines!",
    subtitleLine2:
      "Gather beautiful and powerful characters to strengthen your deck!",
    sectionTitle: "The dazzling heroines return",
    sectionBody:
      "ONE PIECE Card Game [EB-05] Extra Booster brings back the heroine theme! ONE PIECE Heroines Edition Vol.2 features many beautiful and powerful women of the series, including Robin and Nami. Add charming — and deadly — new firepower to your deck!",
    specs: [
      { label: "Release date", value: "October 31, 2026 (Sat)" },
      { label: "Game series", value: "ONE PIECE Card Game" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Featuring popular ONE PIECE heroine cards to reinforce deck building",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/eb05-heroines-edition-vol2-01.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/eb05-heroines-edition-vol2-02.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/eb05-heroines-edition-vol2-03.webp",
    ],
    asideTitle: "📍 Ready to collect the heroines?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry ONE PIECE products",
  }),
  campaign({
    id: "gundam-gd06-stardust-trails",
    brand: "gundamcardgame",
    bannerFile: "gd06-stardust-trails.webp",
    releaseDate: "2026-10-31",
    accent: "indigo",
    badgeText: "⏳ Releases 2026/10/31",
    h1Line1: "[GD-06] Booster Pack",
    h1Accent: "「Stardust Trails」",
    subtitleLine1:
      "Warriors' resolve carves new trails across a battlefield of stardust.",
    subtitleLine2:
      "GUNDAM CARD GAME Booster Pack 6 — the Stardust operation begins!",
    sectionTitle: "Soul of Stardust — Clash Across the Cosmos",
    sectionBody:
      "GUNDAM CARD GAME Booster Pack 6, Stardust Trails, returns you to a cosmos thick with smoke and starlight! Experience destiny-charged duels between mobile suits, deploy new units and tactics, and carve your own path to victory among endless stars!",
    specs: [
      { label: "Release date", value: "October 31, 2026" },
      { label: "Game series", value: "GUNDAM CARD GAME" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Mobile suits and pilots from the Stardust operation, plus new tactical cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd06-stardust-trails-01.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd06-stardust-trails-02.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd06-stardust-trails-03.webp",
    ],
    asideTitle: "📍 Ready for the Stardust operation?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Gundam products",
  }),
  campaign({
    id: "mtg-reality-fracture",
    brand: "magicwizards",
    bannerFile: "mtg-reality-fracture.webp",
    releaseDate: "2026-10-02",
    accent: "purple",
    badgeText: "⏳ Releases 2026/10/02",
    h1Line1: "Magic: The Gathering — New Set",
    h1Accent: "「Reality Fracture」",
    subtitleLine1: "New mechanics and Booster Fun details revealed!",
    subtitleLine2:
      "The legendary strategy card game — where reality and illusion crack apart.",
    sectionTitle: "Reality Fractures — New Mechanics Arrive",
    sectionBody:
      "Wizards of the Coast presents the latest Magic: The Gathering set, Reality Fracture. The original strategy card game with over 50 million players brings powerful new mechanics and collector-focused Booster Fun treatments. In the cracks of reality, discover endless magical power!",
    specs: [
      {
        label: "Release date",
        value:
          "October 2, 2026 (Fri) (local game stores: September 25 early release)",
      },
      { label: "Game series", value: "Magic: The Gathering" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Powerful new-mechanic cards, Booster Fun specialty frames, and foils",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-reality-fracture-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-reality-fracture-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-reality-fracture-3.webp",
    ],
    asideTitle: "📍 Ready to cross the fracture?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Magic: The Gathering products",
  }),
  campaign({
    id: "dragonball-fb11",
    brand: "dragonballcardgamefusionworld",
    bannerFile: "fb11-brightness-of-hope.webp",
    releaseDate: "2026-09-12",
    accent: "amber",
    badgeText: "⏳ Releases 2026/09/12",
    h1Line1: "[FB-11] Booster Pack",
    h1Accent: "「BRIGHTNESS OF HOPE」",
    subtitleLine1: "The light of hope — Super Saiyan 3 crashes onto the stage!",
    subtitleLine2:
      "Dragon Ball Super Fusion World Expansion Pack 11 hits hard.",
    sectionTitle: "Break Your Limits — Ignite the Light of Hope",
    sectionBody:
      "The latest Dragon Ball Super Card Game Fusion World expansion, BRIGHTNESS OF HOPE, is here! Led by Super Saiyan 3 Son Goku, all 123 cards bring even hotter battles. Gather the light of hope, shatter despair, and show your true strength on the table!",
    specs: [
      { label: "Release date", value: "September 12, 2026" },
      {
        label: "Game series",
        value: "DRAGON BALL SUPER CARD GAME Fusion World",
      },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "123 cards total, featuring Super Saiyan 3 and hope-themed power cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/fb11-brightness-of-hope-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/fb11-brightness-of-hope-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/fb11-brightness-of-hope-3.webp",
    ],
    asideTitle: "📍 Ready to ignite your ki?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Dragon Ball products",
  }),

  // —— Archived (kept for old URLs / sitemap / prerender; not on banner) ——
  campaign({
    id: "pokemon-storm-emerald",
    brand: "ptcg",
    bannerFile: "m6-storm-emeralda.webp",
    releaseDate: "2026-07-31",
    accent: "emerald",
    badgeText: "⏳ Releases 2026/07/31",
    h1Line1: "MEGA Booster Set",
    h1Accent: "Storm Emerald (ストームエメラルド)",
    subtitleLine1: "The legendary ruler — Mega Rayquaza ex arrives!",
    subtitleLine2: "Ride the emerald storm and rule the endless sky.",
    sectionTitle: "Emerald Storm — Sweeping the Skies",
    sectionBody:
      "The latest Pokémon TCG series, MEGA: Storm Emerald, brings mighty power that rules the skies! The legendary ruler Mega Rayquaza ex descends, tearing through every obstacle with a raging emerald storm. Master this legendary force from the heavens and command the winds in battle!",
    specs: [
      { label: "Release date", value: "July 31, 2026 (Fri)" },
      { label: "Game series", value: "Pokémon Card Game MEGA" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value: "Mega Rayquaza ex plus storm-themed Pokémon and Trainer cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m6-storm-emeralda-5.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m6-storm-emeralda-6.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m6-storm-emeralda-7.webp",
    ],
    asideTitle: "📍 Ready to ride the storm?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Pokémon products",
  }),
  campaign({
    id: "pokemon-abyss-eye",
    brand: "ptcg",
    bannerFile: "m5-abyss-eye.webp",
    releaseDate: "2026-05-22",
    accent: "purple",
    badgeText: "⏳ Releases 2026/05/22",
    h1Line1: "MEGA Booster Set",
    h1Accent: "Abyss Eye (アビスアイ)",
    subtitleLine1: "The gaze of darkness — Mega Darkrai ex strikes!",
    subtitleLine2:
      "Will you be swallowed by the endless abyss, or master the power of darkness?",
    sectionTitle: "The Abyss Descends — A Nightmare's Dominion",
    sectionBody:
      "The latest Pokémon TCG series, MEGA: Abyss Eye, brings a fearsome force of darkness! Mega Darkrai ex, bearer of the Abyss Eye, drags opponents into nightmares they cannot wake from. Seize this unknown power and dominate the metagame!",
    specs: [
      { label: "Release date", value: "May 22, 2026 (Fri)" },
      { label: "Game series", value: "Pokémon Card Game MEGA" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value: "Mega Darkrai ex plus abyss-themed Pokémon and Trainer cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m5-abyss-eye-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m5-abyss-eye-3.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/m5-abyss-eye-4.webp",
    ],
    asideTitle: "📍 Ready to stare into the abyss?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Pokémon products",
  }),
  campaign({
    id: "onepiece-op17",
    brand: "onepiececardgame",
    bannerFile: "op17-sekaisaikyo.webp",
    releaseDate: "2026-08-22",
    accent: "red",
    badgeText: "⏳ Releases 2026/08/22",
    h1Line1: "[OP-17] Booster Pack",
    h1Accent: "「世界最強の戦士」",
    subtitleLine1: "The world-shaking great war returns...!",
    subtitleLine2:
      "Booster Pack 17, themed around the legendary voyage to Elbaph.",
    sectionTitle: "Challenge the World's Strongest — Set Foot on Elbaph",
    sectionBody:
      "ONE PIECE Card Game Booster Pack 17 arrives, celebrating the game's 4th anniversary! This set takes the stage to Elbaph, gathering the world's strongest warriors. Rekindle your bonds and set out on a brand-new adventure!",
    specs: [
      { label: "Release date", value: "August 22, 2026 (Sat)" },
      { label: "Game series", value: "ONE PIECE Card Game" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value: "Featuring Elbaph, giant warriors, and adventure-themed cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/op17-sekaisaikyo-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/op17-sekaisaikyo-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/op17-sekaisaikyo-3.webp",
    ],
    asideTitle: "📍 Ready to set sail for Elbaph?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry ONE PIECE products",
  }),
  campaign({
    id: "onepiece-op16",
    brand: "onepiececardgame",
    bannerFile: "thetimeofbattle-op16.webp",
    releaseDate: "2026-05-30",
    accent: "red",
    badgeText: "⏳ Releases 2026/05/30",
    h1Line1: "[OP-16] Booster Pack",
    h1Accent: "Moment of Decisive Battle",
    subtitleLine1: "The world-shaking great war returns...!",
    subtitleLine2:
      "Booster Pack 16, themed around the legendary Paramount War.",
    sectionTitle: "Paramount War — A Classic Replayed",
    sectionBody:
      "ONE PIECE Card Game Booster Pack 16 returns to the legendary Paramount War arc! The Navy Headquarters' absolute justice clashes with the Whitebeard Pirates once more—right on your tabletop. Rally your crew, harness the bonds of brotherhood, and change the fate of the world!",
    specs: [
      { label: "Release date", value: "May 30, 2026 (Sat)" },
      { label: "Game series", value: "ONE PIECE Card Game" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Featuring Navy Headquarters and Whitebeard Pirates crossover cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/thetimeofbattle-op16-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/thetimeofbattle-op16-3.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/thetimeofbattle-op16-4.webp",
    ],
    asideTitle: "📍 Ready to join the Paramount War?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry ONE PIECE products",
  }),
  campaign({
    id: "gundam-freedom-ascension",
    brand: "gundamcardgame",
    bannerFile: "gd05-freedom-ascension.webp",
    releaseDate: "2026-07-24",
    accent: "blue",
    badgeText: "⏳ Releases 2026/07/24",
    h1Line1: "[GD-05] Booster Pack",
    h1Accent: "Freedom Ascension",
    subtitleLine1: "Celebrating one year of GUNDAM CARD GAME!",
    subtitleLine2:
      "Fan-favorite Strike Freedom Gundam and ν Gundam make their long-awaited debut.",
    sectionTitle: "Wings of Freedom — Cutting Through the Stars",
    sectionBody:
      "GUNDAM CARD GAME marks its memorable first anniversary! Booster Pack 5, Freedom Ascension, brings the most iconic legendary mobile suits. The addition of Strike Freedom Gundam and ν Gundam will completely change the battlefield. Pilots, spread your wings of freedom and claim a new victory!",
    specs: [
      { label: "Release date", value: "July 24, 2026" },
      { label: "Game series", value: "GUNDAM CARD GAME" },
      { label: "Language", value: "English" },
      {
        label: "Contents",
        value:
          "First-anniversary special: Strike Freedom Gundam, ν Gundam, and other fan-favorite mobile suits",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd05-freedom-ascension-6.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd05-freedom-ascension-7.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd05-freedom-ascension-8.webp",
    ],
    asideTitle: "📍 Ready to board for battle?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Gundam products",
  }),
  campaign({
    id: "gundam-phantom-aria",
    brand: "gundamcardgame",
    bannerFile: "gd04-phanyomaria.webp",
    releaseDate: "2026-04-25",
    accent: "emerald",
    badgeText: "⏳ Releases 2026/04/25",
    h1Line1: "[GD-04] Booster Pack",
    h1Accent: "Phantom Aria (ファントム アリア)",
    subtitleLine1: "A phantom sonata across the cosmos...",
    subtitleLine2: "GUNDAM CARD GAME Booster Pack 4 — Unicorn awakens!",
    sectionTitle: "NT-D Activated — Phantom Awakens",
    sectionBody:
      "GUNDAM CARD GAME Booster Pack 4, Phantom Aria, takes the battlefield deep into space! Led by the Unicorn Gundam, powerful mobile suits and Psycho-Frame technology bring revolutionary tactical shifts. Lead your pilots and sound the victory aria among the stars!",
    specs: [
      { label: "Release date", value: "April 25, 2026" },
      { label: "Game series", value: "GUNDAM CARD GAME" },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Featuring Unicorn Gundam-series mobile suits and dedicated Pilot cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd04-phanyomaria-3.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd04-phanyomaria-4.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/gd04-phanyomaria-5.webp",
    ],
    asideTitle: "📍 Ready to launch into battle?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Gundam products",
  }),
  campaign({
    id: "mtg-marvel-super-heroes",
    brand: "magicwizards",
    bannerFile: "marvel-super-heroes.webp",
    releaseDate: "2026-06-26",
    accent: "amber",
    badgeText: "⏳ Releases 2026/06/26",
    h1Line1: "Magic: The Gathering Crossover",
    h1Accent: "Marvel Super Heroes",
    subtitleLine1: "PLAYERS ASSEMBLE!",
    subtitleLine2:
      "Two iconic worlds collide for Magic's biggest crossover yet!",
    sectionTitle: "Legendary Power, In Your Hands",
    sectionBody:
      "Magic: The Gathering teams up with Marvel Super Heroes for an unprecedented crossover. Your favorite heroes and villains of Earth are now cards to collect, play, and build decks around—put legendary power right in your hands!",
    specs: [
      { label: "Release date", value: "June 26, 2026" },
      {
        label: "Game series",
        value: "Magic: The Gathering Universes Beyond",
      },
      { label: "Language", value: "English" },
      {
        label: "Contents",
        value:
          "Exclusive Marvel Super Heroes and villains cards, plus new mechanics",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/marvel-super-heroes-3.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/marvel-super-heroes-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/marvel-super-heroes-2.webp",
    ],
    asideTitle: "📍 Ready to assemble your heroes?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Magic: The Gathering products",
  }),
  campaign({
    id: "mtg-strixhaven",
    brand: "magicwizards",
    bannerFile: "secrets-of-strixhaven.webp",
    releaseDate: "2026-04-24",
    accent: "green",
    badgeText: "⏳ Releases 2026/04/24",
    h1Line1: "Magic: The Gathering Booster Set",
    h1Accent: "Secrets of Strixhaven",
    subtitleLine1: "School just got real.",
    subtitleLine2:
      "Put down the textbooks and uncover the truths hidden behind the five colleges!",
    sectionTitle: "Trials of the Mage School — The Truth Revealed",
    sectionBody:
      "Welcome back to Strixhaven! In Secrets of Strixhaven, life at the magical university is turned upside down. Students from all five colleges must leave their books behind, step into the real world, and uncover ancient secrets buried in the school's history. Cast every spell you can to prove your college is the strongest!",
    specs: [
      { label: "Release date", value: "April 24, 2026" },
      { label: "Game series", value: "Magic: The Gathering" },
      { label: "Language", value: "English" },
      {
        label: "Contents",
        value:
          "Signature spells from all five colleges, Planeswalkers, and new legendary creatures",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/secrets-of-strixhaven-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/secrets-of-strixhaven-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/secrets-of-strixhaven-5.webp",
    ],
    asideTitle: "📍 Ready for the enrollment challenge?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Magic: The Gathering products",
  }),
  campaign({
    id: "mtg-hobbit",
    brand: "magicwizards",
    bannerFile: "mtg-hobbit.webp",
    releaseDate: "2026-08-14",
    accent: "emerald",
    badgeText: "⏳ Available now",
    h1Line1: "Magic: The Gathering Crossover",
    h1Accent: "「THE HOBBIT」",
    subtitleLine1: "GO THERE AND BACK AGAIN",
    subtitleLine2: "Join Bilbo on an epic adventure — there and back again!",
    sectionTitle: "An Unexpected Journey",
    sectionBody:
      "Join Bilbo on an unforgettable adventure! Make friends with dwarves, outwit trolls, match wits with elves, and sing songs of adventure. At the journey's end there may be gold—or a dragon's fire. Enjoy the ride!",
    specs: [
      { label: "Release date", value: "Available now" },
      {
        label: "Game series",
        value: "Magic: The Gathering Universes Beyond",
      },
      { label: "Language", value: "English" },
      {
        label: "Contents",
        value:
          "Exclusive The Hobbit cards, classic characters, and new mechanics",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-hobbit-1.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-hobbit-2.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/mtg-hobbit-3.webp",
    ],
    asideTitle: "📍 Ready to set out on the journey?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and grab yours today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Magic: The Gathering products",
  }),
  campaign({
    id: "dragonball-fb10",
    brand: "dragonballcardgamefusionworld",
    bannerFile: "fb10-crossforce.webp",
    releaseDate: "2026-06-12",
    accent: "blue",
    badgeText: "⏳ Releases 2026/06/12",
    h1Line1: "[FB-10] Booster Pack",
    h1Accent: "CROSS FORCE",
    subtitleLine1:
      "Goku and Vegeta's ultimate clash — fusion power crosses every boundary!",
    subtitleLine2: "Dragon Ball Super Fusion World Booster Pack 10 is here.",
    sectionTitle: "Beyond the Limits — Battle Returns",
    sectionBody:
      "Dragon Ball Super Card Game Fusion World's latest expansion, CROSS FORCE, delivers an unprecedented battle experience! The power of Super Saiyan Blue erupts on the tabletop—build your ultimate warrior lineup, unleash fusion and boundary-crossing strength, and crush your opponents!",
    specs: [
      { label: "Release date", value: "June 12, 2026" },
      {
        label: "Game series",
        value: "DRAGON BALL SUPER CARD GAME Fusion World",
      },
      { label: "Language", value: "English" },
      {
        label: "Contents",
        value:
          "Featuring Super Saiyan Blue and other powerful character cards, plus new tactical cards",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/fb10-crossforce-4.webp",
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/fb10-crossforce-5.webp",
    ],
    asideTitle: "📍 Ready to unleash your power?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Dragon Ball products",
  }),
  campaign({
    id: "dragonball-story-booster-01",
    brand: "dragonballcardgamefusionworld",
    bannerFile: "story-booster-01.webp",
    releaseDate: "2026-08-08",
    accent: "amber",
    badgeText: "⏳ Releases 2026/08/08",
    h1Line1: "[ST-01] Story Booster",
    h1Accent: "STORY BOOSTER 01",
    subtitleLine1:
      "Classic battles return — Super Saiyan 3 Goku faces Majin Buu!",
    subtitleLine2:
      "Dragon Ball Super Fusion World's first Story Booster arrives.",
    sectionTitle: "Clash with Buu — Nostalgia Hits Hard",
    sectionBody:
      "Dragon Ball Super Card Game Fusion World's brand-new series, STORY BOOSTER 01, is here! Relive the classic Majin Buu Saga as Super Saiyan 3 Son Goku and Pure Majin Buu clash once more on the tabletop. With 85 cards in total, step back into those unforgettable moments!",
    specs: [
      { label: "Release date", value: "August 8, 2026" },
      {
        label: "Game series",
        value: "DRAGON BALL SUPER CARD GAME Fusion World",
      },
      { label: "Language", value: "Japanese" },
      {
        label: "Contents",
        value:
          "Classic characters and iconic scenes from the Majin Buu Saga (85 cards total)",
      },
    ],
    images: [
      "https://creative.cdn.apm.any-digital.com/h5/findpoketcgstore/product-page-images/story-booster-01-1.webp",
    ],
    asideTitle: "📍 Ready to relive the classics?",
    asideBody:
      "Want the latest booster packs? Find an official certified card shop near you and pre-order today!",
    ctaText: "Find certified stores",
    filterHint: "We'll filter stores that carry Dragon Ball products",
  }),

  // —— Newly added (order not finalized; adjust manually for banner) ——
];

function normalizeSlug(raw: string): string {
  try {
    return decodeURIComponent(raw.trim());
  } catch {
    return raw.trim();
  }
}

export function getProductInformationBySlug(
  slug: string | undefined,
): ProductInformationEntry | undefined {
  if (!slug) return undefined;
  const key = normalizeSlug(slug);
  return PRODUCT_INFORMATION_LIST.find((p) => p.slug === key);
}

export function getProductInformationByBrandSlug(
  brand: string | undefined,
  slug: string | undefined,
): ProductInformationEntry | undefined {
  if (!brand || !slug) return undefined;
  const brandKey = brand.toLowerCase();
  const slugKey = normalizeSlug(slug).toLowerCase();
  return PRODUCT_INFORMATION_LIST.find(
    (p) => p.brand === brandKey && p.slug.toLowerCase() === slugKey,
  );
}

/** Home carousel slides — newest {@link BANNER_DISPLAY_COUNT} campaigns. */
export function getActiveBannerSlides(): Array<{
  name: string;
  title: string;
  productPath: string;
}> {
  return PRODUCT_INFORMATION_LIST.slice(0, BANNER_DISPLAY_COUNT).map(
    (entry) => ({
      name: entry.bannerFile,
      title: entry.slug,
      productPath: `/${entry.brand}/${entry.slug}`,
    }),
  );
}

/** All campaign paths for sitemap / react-snap / nginx indexable routes. */
export function getAllCampaignPaths(): string[] {
  return PRODUCT_INFORMATION_LIST.map(
    (entry) => `/${entry.brand}/${entry.slug}`,
  );
}

export { BANNER_DISPLAY_COUNT, bannerMaterialSrc };
